<script setup lang="ts">
import { Button, Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@zerohuman/ui'
import { RouterLink } from 'vue-router'
import type { FoundedEnterprise } from '@/api'

/**
 * #2218: the notice on the first screen after a sign-in that founded a new enterprise. Somebody invited at their work
 * address who signs in with another account becomes a new person with a new, empty enterprise, and may take it for
 * their team's. This says plainly that it was just made for them, then either:
 * - names each enterprise whose invitation is waiting for an address they signed in with, and points to that
 *   invitation's email. Never a link to it: only its hash is kept, and pointing to one admits nobody; or
 * - with none waiting, says how to get in with the address an invitation was sent to.
 *
 * It offers a way out: sign out, or close the enterprise in #72's owner-only Danger zone on Enterprise (the founder is
 * always its owner). Dismissing it is the screen's to remember. Built only from the kit's `Card` and `Button`.
 *
 * Signing out is the screen's too: while it is under way the button is disabled and says so, and when it fails the
 * notice says it did not go through, so the person can try again rather than wonder whether the button works.
 */
withDefaults(defineProps<{ founded: FoundedEnterprise; signingOut?: boolean; signOutFailed?: boolean }>(), {
    signingOut: false,
    signOutFailed: false,
})

const emit = defineEmits<{ signOut: []; dismiss: [] }>()

/** #72's Danger zone on Enterprise (`EnterpriseDangerZone`), by the id of its heading. */
const CLOSE_SECTION = '/enterprise#enterprise-danger-zone-heading'
</script>

<template>
	<Card role="region" aria-labelledby="founded-enterprise-title" data-testid="founded-enterprise-notice">
		<CardHeader>
			<CardTitle id="founded-enterprise-title">Your new enterprise</CardTitle>
			<CardDescription>
				We've made a new enterprise for you: <strong class="text-foreground">{{ founded.name }}</strong>. You
				own it, and it's empty.
			</CardDescription>
		</CardHeader>
		<CardContent class="space-y-2 text-sm">
			<template v-if="founded.invitations.length > 0">
				<p>Waiting for an address you signed in with:</p>
				<!-- Keyed by place: enterprise names are not unique, and the list is never reordered. -->
				<ul class="list-disc space-y-1 pl-5">
					<li
						v-for="(invitation, index) in founded.invitations"
						:key="index"
						data-testid="founded-notice-invitation"
					>
						An invitation from <strong>{{ invitation.enterpriseName }}</strong>, sent to
						{{ invitation.email }}.
					</li>
				</ul>
				<p>
					To join, open the invitation email sent to that address and use its link. If you can't find it, ask
					whoever invited you to send it again.
				</p>
			</template>
			<p v-else>
				If you meant to join your team, sign out and sign in with the address your invitation was sent to, or
				open the link in that email.
			</p>
			<p v-if="signOutFailed" role="alert" class="text-destructive text-sm" data-testid="founded-notice-sign-out-failed">
				We couldn't sign you out. Try again.
			</p>
		</CardContent>
		<CardFooter class="flex-wrap gap-2">
			<Button
				type="button"
				size="sm"
				variant="outline"
				:disabled="signingOut"
				:aria-busy="signingOut"
				data-testid="founded-notice-sign-out"
				@click="emit('signOut')"
			>
				{{ signingOut ? 'Signing out…' : 'Sign out' }}
			</Button>
			<Button as-child size="sm" variant="ghost">
				<RouterLink :to="CLOSE_SECTION" data-testid="founded-notice-close-link">Close this enterprise</RouterLink>
			</Button>
			<Button
				type="button"
				size="sm"
				variant="ghost"
				class="ml-auto"
				data-testid="founded-notice-dismiss"
				@click="emit('dismiss')"
			>
				Dismiss
			</Button>
		</CardFooter>
	</Card>
</template>
